import React from "react";
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from "recharts";

// Chart palette — each y-key picks the next color. The first two are the most
// visually distinct so the primary comparison (e.g. prior year vs current year)
// is instantly legible.
const COLORS = [
  "#0d3880", // brand navy — current / primary series
  "#c2410c", // burnt amber — secondary comparison series
  "#059669", // emerald — third series
  "#7c3aed", // violet — fourth
  "#0ea5e9", // sky
  "#be123c", // rose
];
// Axes stay neutral graphite so the colored data lines remain the hero.
const AXIS_STROKE = "#64748b";
const AXIS_LINE = "#475569";
const TICK_COLOR = "#475569";
const GRID_STROKE = "rgba(71,85,105,0.15)";

const lightTooltip = {
  contentStyle: {
    background: "rgba(255,255,255,0.98)",
    border: "1px solid rgba(13,56,128,0.2)",
    borderRadius: 2,
    color: "#0b1a2b",
    fontSize: 12,
    boxShadow: "0 4px 16px rgba(13,56,128,0.10)",
  },
  labelStyle: { color: "#0d3880", textTransform: "uppercase", letterSpacing: "0.15em", fontSize: 10, fontWeight: 600 },
  itemStyle: { color: "#0b1a2b" },
  cursor: { fill: "rgba(13,56,128,0.05)" },
  // Comma-separated, 3-decimal-max formatting so tooltips never show raw unformatted numbers.
  formatter: (v) => {
    if (v == null || isNaN(v)) return v;
    const n = Number(v);
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 3 }).format(n);
  },
};

const ChartBlock = ({ spec, data, height = 300, compact = false }) => {
  if (!spec || !data?.length) return null;
  const xKey = spec.x_key || "name";
  const yKeys = spec.y_keys?.length ? spec.y_keys : ["value"];

  if (spec.kind === "line") {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 10, right: 15, left: compact ? 0 : 10, bottom: compact ? 0 : 10 }}>
          <CartesianGrid stroke={GRID_STROKE} vertical={false} />
          <XAxis dataKey={xKey} stroke={AXIS_STROKE} tick={{ fill: TICK_COLOR, fontSize: 10 }} tickLine={false} axisLine={{ stroke: AXIS_LINE, strokeWidth: 1 }} />
          <YAxis stroke={AXIS_STROKE} tick={{ fill: TICK_COLOR, fontSize: 10 }} tickLine={false} axisLine={{ stroke: AXIS_LINE, strokeWidth: 1 }} tickFormatter={(v) => Intl.NumberFormat("en", { notation: "compact" }).format(v)} />
          <Tooltip {...lightTooltip} />
          {!compact && <Legend wrapperStyle={{ fontSize: 11, color: "#6b7a94" }} />}
          {yKeys.map((k, i) => (
            <Line key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: COLORS[i % COLORS.length] }} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }
  if (spec.kind === "donut") {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={data} dataKey={yKeys[0]} nameKey={xKey} innerRadius={compact ? 40 : 70} outerRadius={compact ? 70 : 110} paddingAngle={1} stroke="#ffffff">
            {data.map((_, i) => (
              <Cell key={i} fill={COLORS[i % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip {...lightTooltip} />
          {!compact && <Legend wrapperStyle={{ fontSize: 11, color: "#6b7a94" }} />}
        </PieChart>
      </ResponsiveContainer>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 10, right: 15, left: compact ? 0 : 10, bottom: compact ? 0 : 10 }}>
        <CartesianGrid stroke={GRID_STROKE} vertical={false} />
        <XAxis dataKey={xKey} stroke={AXIS_STROKE} tick={{ fill: TICK_COLOR, fontSize: 10 }} tickLine={false} axisLine={{ stroke: AXIS_LINE, strokeWidth: 1 }} angle={compact ? 0 : -20} height={compact ? 20 : 40} textAnchor={compact ? "middle" : "end"} />
        <YAxis stroke={AXIS_STROKE} tick={{ fill: TICK_COLOR, fontSize: 10 }} tickLine={false} axisLine={{ stroke: AXIS_LINE, strokeWidth: 1 }} tickFormatter={(v) => Intl.NumberFormat("en", { notation: "compact" }).format(v)} />
        <Tooltip {...lightTooltip} />
        {!compact && <Legend wrapperStyle={{ fontSize: 11, color: "#6b7a94" }} />}
        {yKeys.map((k, i) => (
          <Bar key={k} dataKey={k} fill={COLORS[i % COLORS.length]} radius={[2, 2, 0, 0]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ChartBlock;
